import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const AUTH_ACTIONS = new Set(["login_success", "login_failed", "logout", "reset_password"]);
const BRUTE_FORCE_THRESHOLD = 5;   // ≥5 failures from the same IP in 1h
const BRUTE_FORCE_WINDOW_MS = 60 * 60 * 1000;

export default function AdminAudit() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all"); // all | auth | mutations | failures
  const [clearing, setClearing] = useState("");

  const load = () => api.get("/audit?limit=500")
    .then((r) => setList(r.data))
    .catch((e) => toast.error(formatErr(e)));

  useEffect(() => { load(); }, []);

  const clearLockout = async (ip) => {
    if (!window.confirm(`Clear lockout for ${ip}?\n\nThis lets anyone on that IP retry the login immediately.`)) return;
    setClearing(ip);
    try {
      await api.post("/admin/lockout/clear", { ip });
      toast.success(`Cleared lockout for ${ip}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setClearing(""); }
  };

  const bruteForce = useMemo(() => {
    const cutoff = Date.now() - BRUTE_FORCE_WINDOW_MS;
    const byIp = new Map();
    for (const l of list) {
      if (l.action !== "login_failed" || !l.ip) continue;
      if (new Date(l.at).getTime() < cutoff) continue;
      const row = byIp.get(l.ip) || { ip: l.ip, count: 0, emails: new Set(), latest: l.at };
      row.count += 1;
      row.emails.add(l.record);
      if (l.at > row.latest) row.latest = l.at;
      byIp.set(l.ip, row);
    }
    return [...byIp.values()]
      .filter((r) => r.count >= BRUTE_FORCE_THRESHOLD)
      .map((r) => ({ ...r, emails: [...r.emails] }))
      .sort((a, b) => b.count - a.count);
  }, [list]);

  const filtered = useMemo(() => {
    if (filter === "all") return list;
    if (filter === "auth") return list.filter((l) => AUTH_ACTIONS.has(l.action));
    if (filter === "failures") return list.filter((l) => l.action === "login_failed");
    if (filter === "roles") return list.filter((l) => l.action === "role_change");
    return list.filter((l) => !AUTH_ACTIONS.has(l.action));
  }, [list, filter]);

  const chip = (key, label, count) => (
    <button key={key} onClick={() => setFilter(key)}
      data-testid={`audit-filter-${key}`}
      className={`px-3 py-1 text-xs border transition-colors duration-150 ${
        filter === key ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "bg-white text-neutral-700 border-[#E5E5E5] hover:border-[#1F4E8C]"
      }`}>
      {label} <span className="opacity-70 ml-1">· {count}</span>
    </button>
  );

  const counts = useMemo(() => ({
    all: list.length,
    auth: list.filter((l) => AUTH_ACTIONS.has(l.action)).length,
    failures: list.filter((l) => l.action === "login_failed").length,
    roles: list.filter((l) => l.action === "role_change").length,
    mutations: list.filter((l) => !AUTH_ACTIONS.has(l.action)).length,
  }), [list]);

  return (
    <div data-testid="admin-audit">
      <PageHeader overline="Security" title="Audit Logs" subtitle="Immutable trail of all critical actions, including every login attempt." />

      {bruteForce.length > 0 && (
        <div className="mb-6 border-l-4 border-red-600 bg-red-50 p-4" data-testid="brute-force-alert">
          <div className="font-semibold text-red-800 text-sm mb-2">
            ⚠ {bruteForce.length} IP{bruteForce.length === 1 ? "" : "s"} with ≥{BRUTE_FORCE_THRESHOLD} failed logins in the last hour
          </div>
          <div className="text-xs text-red-900/80 space-y-1">
            {bruteForce.map((b) => (
              <div key={b.ip} className="flex flex-wrap gap-4 items-center" data-testid={`brute-force-${b.ip}`}>
                <span className="font-mono font-semibold">{b.ip}</span>
                <span>{b.count} failures</span>
                <span className="text-red-800/70">targets: {b.emails.join(", ")}</span>
                <span className="text-red-800/70">latest: {new Date(b.latest).toLocaleString()}</span>
                <button
                  onClick={() => clearLockout(b.ip)}
                  disabled={clearing === b.ip}
                  data-testid={`clear-lockout-${b.ip}`}
                  className="ml-auto text-xs underline underline-offset-2 text-red-700 hover:text-red-900 disabled:opacity-50">
                  {clearing === b.ip ? "Clearing…" : "Clear lockout →"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {chip("all", "All", counts.all)}
        {chip("auth", "Auth events", counts.auth)}
        {chip("failures", "Failed logins", counts.failures)}
        {chip("roles", "Role changes", counts.roles)}
        {chip("mutations", "Mutations", counts.mutations)}
      </div>

      <Section title={`${filtered.length} entries`}>
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full font-mono text-xs min-w-[760px]">
            <thead>
              <tr className="border-b-2 border-[#1F4E8C] text-left">
                <th className="eyebrow py-2 pr-3">When</th>
                <th className="eyebrow py-2 pr-3">Action</th>
                <th className="eyebrow py-2 pr-3">Who</th>
                <th className="eyebrow py-2 pr-3">Record</th>
                <th className="eyebrow py-2 pr-3">IP</th>
                <th className="eyebrow py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const isFail = l.action === "login_failed";
                const isSuccess = l.action === "login_success";
                return (
                  <tr key={l.id} className={`border-b border-[#F0F0EE] ${isFail ? "bg-red-50/50" : ""}`}>
                    <td className="py-2 pr-3 text-neutral-500 whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                    <td className={`py-2 pr-3 font-semibold whitespace-nowrap ${isFail ? "text-red-700" : isSuccess ? "text-emerald-700" : "text-[#FF5500]"}`}>{l.action}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{l.user_name || "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{l.record || "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-neutral-600">{l.ip || "—"}</td>
                    <td className="py-2 text-neutral-600 whitespace-nowrap">{l.details}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={6} className="text-neutral-500 py-6 text-center">No matching audit entries</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
